export const BDC_PATH = "/bdc";
export const BDC_IMPORT_PATH = "/bdc/import";
export const BDC_QUEUE_PATH = "/bdc/queue";
export const BDC_SETTINGS_PATH = "/bdc/settings";
export const BDC_SHIPMENT_PATH = "/bdc/shipments/:poNumber";
export const BDC_STOCK_DAY_PATH = "/bdc/stock-day";
export const BDC_DISPATCH_PATH = "/bdc/dispatch";
export const BDC_DOCUMENTS_PATH = "/bdc/documents";
export const BDC_INVOICES_PATH = "/bdc/invoices";
export const BDC_EDI_PATH = "/bdc/edi";
export const BDC_CREDIT_NOTES_PATH = "/bdc/credit-notes";
/** One CN: `new` opens an empty draft (`?invoice_no=` starts it from that INV). */
export const BDC_CREDIT_NOTE_PATH = "/bdc/credit-notes/:uuid";
export const BDC_BILLINGS_PATH = "/bdc/billings";

export const bdcCreditNoteLink = (uuid: string) => `${BDC_CREDIT_NOTES_PATH}/${encodeURIComponent(uuid)}`;
/** A new CN draft, optionally started from one INV (ทะเบียน INV › สร้าง CN). */
export const bdcNewCreditNoteLink = (invoiceNo?: string) =>
  `${BDC_CREDIT_NOTES_PATH}/new${invoiceNo ? `?invoice_no=${encodeURIComponent(invoiceNo)}` : ""}`;

export const bdcShipmentLink = (poNumber: string, tab?: string) =>
  `/bdc/shipments/${encodeURIComponent(poNumber)}${tab ? `?tab=${tab}` : ""}`;

/** ตั้งค่า BDC on one tab — `dc` is where an unbound DC gets its branch. */
export const bdcSettingsLink = (tab?: "branch" | "dc" | "store" | "sku" | "invoice") =>
  tab ? `${BDC_SETTINGS_PATH}?tab=${tab}` : BDC_SETTINGS_PATH;

/** ขาด-เกิน of one order date, or the GM report (`view=report`). */
export const bdcStockDayLink = (workDate?: string) =>
  workDate ? `${BDC_STOCK_DAY_PATH}?date=${workDate}` : BDC_STOCK_DAY_PATH;

/** ใบจ่ายสินค้า of one order date, optionally opening one DC's note. */
export const bdcDispatchLink = (workDate?: string, dcCode?: string) => {
  const query = new URLSearchParams();
  if (workDate) query.set("date", workDate);
  if (dcCode) query.set("dc", dcCode);
  const text = query.toString();
  return text ? `${BDC_DISPATCH_PATH}?${text}` : BDC_DISPATCH_PATH;
};

export const bdcQueueLink = (orderDate?: string) =>
  orderDate ? `${BDC_QUEUE_PATH}?order_date=${orderDate}` : BDC_QUEUE_PATH;

/** Menu titles; the sidebar matches gated entries by title. */
export const BDC_MENU_TITLE = "BDC";
export const BDC_QUEUE_MENU_TITLE = "คิวงาน";
export const BDC_IMPORT_MENU_TITLE = "นำเข้าไฟล์";
export const BDC_DISPATCH_MENU_TITLE = "ใบจ่ายสินค้า";
export const BDC_STOCK_DAY_MENU_TITLE = "ขาด-เกิน";
export const BDC_DOCUMENTS_MENU_TITLE = "ศูนย์เอกสาร";
export const BDC_EDI_MENU_TITLE = "ไฟล์ส่ง 7-11";
export const BDC_INVOICES_MENU_TITLE = "ทะเบียน INV";
export const BDC_CREDIT_NOTES_MENU_TITLE = "ใบลดหนี้ (CN)";
export const BDC_BILLINGS_MENU_TITLE = "ใบวางบิล";
export const BDC_SETTINGS_MENU_TITLE = "ตั้งค่า BDC";

/** The draft A4 is not a tax invoice — P1 issues no invoice number. */
export const BDC_INVOICE_TITLE = "ใบส่งสินค้า (ร่าง) — ไม่ใช่ใบกำกับภาษี";

export const NO_STORES_LABEL = "ไม่มีรายร้าน";

/** Queue page size — the API's list cap (100); one order day is ~20 shipments. */
export const BDC_QUEUE_LIMIT = 100;

/** Upload caps the API enforces (BdcImportFile): 40 .txt files, 10 MB each. */
export const BDC_IMPORT_MAX_FILES = 40;
export const BDC_IMPORT_MAX_FILE_BYTES = 10 * 1024 * 1024;

/** PATCH /bdc/skus accepts pcs_per_case 1..1000. */
export const BDC_PCS_PER_CASE_MAX = 1000;

/** PUT /bdc/skus/:barcode/pcs-per-tray accepts 1..1000; the BDC's trays hold 40 or 50. */
export const BDC_PCS_PER_TRAY_MAX = 1000;

/** ศูนย์เอกสาร of one day, optionally with shipments pre-ticked (from the queue's selection bar). */
export const bdcDocumentsLink = (opts?: { orderDate?: string; deliveryDate?: string; select?: string[] }) => {
  const query = new URLSearchParams();
  if (opts?.orderDate) query.set("order_date", opts.orderDate);
  if (opts?.deliveryDate) query.set("delivery_date", opts.deliveryDate);
  if (opts?.select?.length) query.set("select", opts.select.join(","));
  const text = query.toString();
  return text ? `${BDC_DOCUMENTS_PATH}?${text}` : BDC_DOCUMENTS_PATH;
};

/** ไฟล์ส่ง 7-11 of one day (it lists a day at a time), optionally with shipments pre-ticked. */
export const bdcEdiLink = (opts?: { orderDate?: string; deliveryDate?: string; select?: string[] }) => {
  const query = new URLSearchParams();
  if (opts?.orderDate) query.set("order_date", opts.orderDate);
  if (opts?.deliveryDate) query.set("delivery_date", opts.deliveryDate);
  if (opts?.select?.length) query.set("select", opts.select.join(","));
  const text = query.toString();
  return text ? `${BDC_EDI_PATH}?${text}` : BDC_EDI_PATH;
};

/** POST /bdc/invoices/:no/cancel — reason 1..500 characters. */
export const BDC_CANCEL_REASON_MAX = 500;

/** INV numbers: prefix + 9 digits (B269005674). */
export const BDC_INVOICE_NO_DIGITS = 9;
export const BDC_INVOICE_NO_MAX = 999_999_999;

/** POST /bdc/documents takes at most 100 shipments. */
export const BDC_DOCUMENTS_MAX_SHIPMENTS = 100;

/** CN numbers: prefix (`C2`, test `TC2`) + 7 digits (C20042870). */
export const BDC_CREDIT_NOTE_NO_DIGITS = 7;

/** The default reason of a CN (spec 3.1). */
export const BDC_CREDIT_NOTE_DEFAULT_REASON = "ขาดส่ง-ชำรุดตอนรับสินค้า";

/** bdc_credit_note.return_doc_no: exactly 10 digits, as the server checks. */
export const BDC_RETURN_DOC_NO_LENGTH = 10;

/** A CN line's อ้างอิง (PO or WB return no.): the server allows 15 characters. */
export const BDC_CREDIT_NOTE_REF_NO_MAX = 15;

/** POST /bdc/edi-exports takes at most 100 CNs per RETSEV run (as for shipments). */
export const BDC_CN_EDI_MAX = 100;

/** GET /bdc/billings/preview returns at most this many rows. */
export const BDC_BILLING_PREVIEW_ROW_CAP = 2000;
